<template>
  <div id="candidate-age-study">
    <vx-card title="Candidatos por edad y estudios">
      <!-- LOADING -->
      <div
        v-if="uiState === 'loading'"
        class="gw-empty-state"
        style="height:350px;"
      >
        <div class="gw-spinner" aria-label="Cargando"></div>
        <div class="gw-empty-title">Cargando información…</div>
        <div class="gw-empty-subtitle">Un momento por favor</div>
      </div>

      <!-- READY (CHART) -->
      <VueApexCharts
        v-else-if="uiState === 'ready' && renderGraphic"
        type="area"
        height="350"
        :options="chartOptions"
        :series="series"
      />

      <!-- EMPTY / ERROR -->
      <div
        v-else
        class="gw-empty-state"
        style="height:350px;"
      >
        <img
          class="gw-empty-img"
          :src="emptyStateImage"
          :alt="emptyStateAlt"
        />
        <div class="gw-empty-title">{{ emptyStateTitle }}</div>
        <div class="gw-empty-subtitle">{{ emptyStateSubtitle }}</div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import { publicAssetUrl } from '@/utils/publicAssets'
import { eventBus } from "./../../main";
import VueApexCharts from "vue-apexcharts";
import api from "../../mixins/api";
import { mapGetters } from "vuex";

export default {
  props: ["startDate", "endDate", "cityId"],
  mixins: [api],

  data() {
    return {
      // UI states: 'loading' | 'ready' | 'empty' | 'error'
      uiState: "loading",
      renderGraphic: false,
      requestKey: null,
      consultHandler: null,

      IMG_EMPTY:
        publicAssetUrl('Assets/illustrations/no_data_found.png'),
      IMG_ERROR:
        publicAssetUrl('Assets/illustrations/error.png'),

      series: [
        { name: "Bachillerato", type: "area", data: [] },
        { name: "Formación superior", type: "line", data: [] },
      ],

      chartOptions: {
        chart: {
          height: 350,
          type: "line",
        },
        stroke: {
          curve: "smooth",
        },
        fill: {
          type: "solid",
          opacity: [0.35, 1],
        },
        labels: ["18-20", "21-25", "26-30", "31-35", "36-40", "41-45", "46-50"],
        markers: {
          size: 0,
        },
        colors: ["#5F0091", "#FFCF44"],
        yaxis: [
          {
            title: { text: "Candidatos" },
          },
          {
            opposite: true,
            title: { text: "Candidatos" },
          },
        ],
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: function (y) {
              if (typeof y !== "undefined" && y !== null) {
                var n = typeof y === "string" ? parseFloat(y) : y;
                if (typeof n === "number" && !isNaN(n)) return n.toFixed(0) + " candidatos";
              }
              return y;
            },
          },
        },
      },
    };
  },

  computed: {
    ...mapGetters(["userInfo"]),

    emptyStateImage() {
      return this.uiState === "error" ? this.IMG_ERROR : this.IMG_EMPTY;
    },
    emptyStateAlt() {
      return this.uiState === "error"
        ? "Se produjo un error con la consulta"
        : "No se encontraron resultados";
    },
    emptyStateTitle() {
      return this.uiState === "error"
        ? "Se produjo un error con la consulta"
        : "Sin datos para el filtro seleccionado";
    },
    emptyStateSubtitle() {
      return this.uiState === "error"
        ? "Inténtalo de nuevo o ajusta los filtros."
        : "Prueba con otro rango de fechas o ciudad.";
    },
  },

  methods: {
    getUrl() {
      let path = `/metrics/candidate-age-study?client_id=${this.userInfo.user.client_id}`;
      let url = "";

      if (this.startDate != null && this.endDate != null) {
        let params = `&start_date=${this.startDate}&end_date=${this.endDate}`;
        url = `${this.url()}${path}${params}`;
      } else {
        url = `${this.url()}${path}`;
      }

      if (this.cityId != null) url += `&city_id=${this.cityId}`;

      return url;
    },

    resetChart() {
      this.renderGraphic = false;
      this.series = [
        { name: "Bachillerato", type: "area", data: [] },
        { name: "Formación superior", type: "line", data: [] },
      ];
    },

    setLoading(key) {
      this.requestKey = key;
      this.uiState = "loading";
      this.resetChart();
    },

    setEmpty() {
      this.uiState = "empty";
      this.resetChart();
    },

    setError() {
      this.uiState = "error";
      this.resetChart();
    },

    hasAnyPositiveInArray(arr) {
      if (!Array.isArray(arr)) return false;
      for (let i = 0; i < arr.length; i++) {
        let v = arr[i];
        if (typeof v === "string") v = parseFloat(v);
        if (typeof v === "number" && !isNaN(v) && v > 0) return true;
      }
      return false;
    },

    async setReady(bachilleratoSeries, superiorSeries) {
      // force remount (evita glitches Apex)
      this.renderGraphic = false;
      await this.$nextTick();

      this.series = [
        { name: "Bachillerato", type: "area", data: bachilleratoSeries || [] },
        { name: "Formación superior", type: "line", data: superiorSeries || [] },
      ];

      this.renderGraphic = true;
      this.uiState = "ready";
    },

    normalizeSeriesFromElement(element) {
      // element suele venir como { algo: [ { "18-20": 10 }, ... ] } o similar
      if (!element || typeof element !== "object") return [];
      const keys = Object.keys(element);
      if (!keys.length) return [];
      const arr = element[keys[0]];
      if (!Array.isArray(arr)) return [];

      const out = [];
      for (let i = 0; i < arr.length; i++) {
        const row = arr[i];
        if (!row || typeof row !== "object") {
          out.push(0);
          continue;
        }
        const rk = Object.keys(row)[0];
        let v = rk ? row[rk] : 0;
        if (typeof v === "string") v = parseFloat(v);
        v = typeof v === "number" && !isNaN(v) ? v : 0;
        out.push(v);
      }
      return out;
    },

    async initializeData(origin) {
      if (!origin) origin = "unknown";

      const key = `${this.startDate || ""}|${this.endDate || ""}|${this.cityId || ""}`;
      this.setLoading(key);

      console.log("[candidate-age-study] initializeData:", {
        origin,
        startDate: this.startDate,
        endDate: this.endDate,
        cityId: this.cityId,
        url: this.getUrl(),
      });

      try {
        const request = await this.requestGetWithoutContent(this.getUrl(), {});

        // race condition guard
        if (this.requestKey !== key) {
          console.log("[candidate-age-study] stale response ignored");
          return;
        }

        if (!request || request.status !== 200) {
          console.warn("[candidate-age-study] request not ok:", request && request.status);
          this.setError();
          return;
        }

        const data = request.data;

        if (!Array.isArray(data) || data.length < 2) {
          console.warn("[candidate-age-study] data invalid:", data);
          this.setEmpty();
          return;
        }

        const bach = this.normalizeSeriesFromElement(data[0]);
        const sup = this.normalizeSeriesFromElement(data[1]);

        const hasAny = this.hasAnyPositiveInArray(bach) || this.hasAnyPositiveInArray(sup);
        if (!hasAny) {
          console.warn("[candidate-age-study] no positive values");
          this.setEmpty();
          return;
        }

        await this.setReady(bach, sup);
      } catch (e) {
        if (this.requestKey !== key) return;
        console.warn("[candidate-age-study] request exception:", e);
        this.setError();
      }
    },
  },

  mounted() {
    this.initializeData("mounted");

    this.consultHandler = () => this.initializeData("eventBus:consultData");
    eventBus.$on("consultData", this.consultHandler);
  },

  beforeDestroy() {
    if (this.consultHandler) eventBus.$off("consultData", this.consultHandler);
  },

  components: {
    VueApexCharts,
  },
};
</script>

<style scoped>
.gw-empty-state{
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap: 10px;
  text-align:center;
  padding: 16px;
}

.gw-empty-img{
  max-width: 260px;
  width: 100%;
  height: auto;
}

.gw-empty-title{
  font-weight: 600;
  font-size: 14px;
  color: #4b4b4b;
}

.gw-empty-subtitle{
  font-size: 12px;
  color: #7a7a7a;
}

.gw-spinner{
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid rgba(95,0,145,0.18);
  border-top-color: rgba(95,0,145,0.9);
  animation: gwspin 0.9s linear infinite;
}

@keyframes gwspin{
  to { transform: rotate(360deg); }
}
</style>
